/**
 * **M-7** — the app build record: `GET /v1/apps/:appId/build` (06b G4, producer B1).
 *
 * ⛔ Its own module, for `appConfig.api.ts`'s reason: `permission-gate-parity` discharges a
 * component's obligation on *any one* permission its imported module declares, so the granularity
 * IS the check.
 */
import { api } from '../platform/http/client';

/**
 * B1's gate: `cp:app:read` on the app.
 *
 * The producer is `AppsController.getBuildStatus` in release-engine (`@Get(':appId/build')`), which
 * carries `@RequireResourcePermission('cp:app:read', 'appId')` since #1084: the handler writes
 * nothing, and the sibling reads of that controller take the same gate. The console mirrors the
 * producer (plan 2026-10-13-console-ward-app-drill-in, A-4). The manifest entry `app-build` gates its
 * route on this value, so a principal who may read the app may open the build record, and a
 * principal who may not gets the 403 panel.
 *
 * It contains `:read`, so `permission-gate-parity.test.ts` enrols this module. `AppBuildPane` meets
 * the rule through the `app-build` route, whose `requiredPermission` is this constant.
 */
export const APP_BUILD_PERMISSION = 'cp:app:read';

/**
 * ⛔ HAND-DECLARED from `AppBuildStatusSummary` and its two element types
 * (`apps.service.ts:81-102`) — release-engine module-local, no `@zion/sdk-contracts` entry.
 */
export interface AppBuildVersionSummary {
  readonly version: string;
  /** ISO-8601 (`apps.service.ts:924` — `.toISOString()`). */
  readonly registeredAt: string;
  readonly status: 'succeeded' | 'failed' | 'running';
}

export interface AppLockFileDiff {
  readonly fixtureName: string;
  readonly fromVersion: string | null;
  readonly toVersion: string | null;
  readonly changeType: 'added' | 'updated' | 'removed';
}

export interface AppBuildStatus {
  readonly latestVersion: string | null;
  /**
   * ⛔ **FOUR values.** `unknown` is the producer's answer when the app has NO composition at all
   * (`apps.service.ts:931`). It is not an error and not a failure — it is a new app.
   */
  readonly latestStatus: 'succeeded' | 'failed' | 'running' | 'unknown';
  readonly registeredVersions: readonly AppBuildVersionSummary[];
  /**
   * ⛔ `[]` when there are fewer than two compositions (`:933-939`) — so *"no previous build to
   * compare against"* and *"nothing changed"* are **the SAME empty array on the wire**. The screen
   * must distinguish them itself; `AppBuildPane` resolves it from `registeredVersions.length`.
   */
  readonly lockFileDiffs: readonly AppLockFileDiff[];
}

export function appBuildPath(appId: string): string {
  return `/v1/apps/${appId}/build`;
}

export function getAppBuildStatus(appId: string): Promise<AppBuildStatus> {
  return api.get<AppBuildStatus>(appBuildPath(appId));
}
